import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, User, Building2, ArrowRight, CheckCircle, Eye, EyeOff,
  Calendar, MapPin, Phone, Mail, Briefcase, ChevronRight, ShieldCheck, Heart
} from 'lucide-react';
import UniversalHeader from '@/components/shared/UniversalHeader';
import UniversalFooter from '@/components/shared/UniversalFooter';
import { toastUtils } from '@/hooks/use-toast';
import { Country, State, City } from 'country-state-city';

type RegistrationStep = 'account-type' | 'account-details' | 'security' | 'complete';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<'individual' | 'provider'>('individual');
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('account-type');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    nextOfKinName: '',
    nextOfKinRelationship: '',
    nextOfKinPhone: ''
  });

  const [providerFormData, setProviderFormData] = useState({
    institutionName: '',
    institutionType: '',
    contactFirstName: '',
    contactLastName: '',
    contactTitle: '',
    website: '',
    keyServices: '',
    street: '',
    city: '',
    country: 'NG', // Default to Nigeria
    state: '',
    zipCode: '',
    npiNumber: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get all countries and states for the selected country
  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(() => {
    if (!providerFormData.country) return [];
    return State.getStatesOfCountry(providerFormData.country);
  }, [providerFormData.country]);
  const cities = useMemo(() => {
    if (!providerFormData.country || !providerFormData.state) return [];
    const selectedState = states.find(s => s.name === providerFormData.state);
    if (!selectedState) return [];
    return City.getCitiesOfState(providerFormData.country, selectedState.isoCode);
  }, [providerFormData.country, providerFormData.state, states]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProviderInputChange = (field: string, value: string) => {
    setProviderFormData(prev => {
      // Reset state and city when country changes
      if (field === 'country') {
        return { ...prev, [field]: value, state: '', city: '' };
      }
      // Reset city when state changes
      if (field === 'state') {
        return { ...prev, [field]: value, city: '' };
      }
      return { ...prev, [field]: value };
    });
  };

  const validateStep = () => {
    if (currentStep === 'account-details') {
      const errors: string[] = [];
      if (userType === 'individual') {
        if (!formData.firstName) errors.push('First Name');
        if (!formData.lastName) errors.push('Last Name');
        if (!formData.dateOfBirth) errors.push('Date of Birth');
        if (!formData.email) errors.push('Email');
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          errors.push('Valid Email');
        }
      } else {
        if (!providerFormData.institutionName) errors.push('Institution Name');
        if (!providerFormData.institutionType) errors.push('Institution Type');
        if (!providerFormData.contactFirstName) errors.push('Contact First Name');
        if (!providerFormData.contactLastName) errors.push('Contact Last Name');
        if (!providerFormData.contactTitle) errors.push('Contact Title');
        if (!formData.email) errors.push('Email');
        if (!formData.phone) errors.push('Phone Number');
        if (!providerFormData.street) errors.push('Street Address');
        if (!providerFormData.city) errors.push('City');
        if (!providerFormData.state) errors.push('State');
      }

      if (errors.length > 0) {
        toastUtils.error('Missing Information', `Please fill in: ${errors.join(', ')}`);
        return false;
      }
      return true;
    }

    if (currentStep === 'security') {
      const errors: string[] = [];
      if (!formData.password) errors.push('Password is required');
      else if (formData.password.length < 8) errors.push('Password must be at least 8 characters');
      if (formData.password !== formData.confirmPassword) errors.push('Passwords must match');

      if (errors.length > 0) {
        toastUtils.error('Security Check', errors.join('. '));
        return false;
      }
      return true;
    }
    return true;
  };

  const handleNext = async () => {
    if (currentStep === 'account-type') {
      setCurrentStep('account-details');
    } else if (currentStep === 'account-details') {
      if (validateStep()) setCurrentStep('security');
    } else if (currentStep === 'security') {
      if (validateStep()) await submitRegistration();
    }
  };

  const submitRegistration = async () => {
    setIsLoading(true);
    try {
      const endpoint = userType === 'individual'
        ? 'http://localhost:3002/api/auth/register/individual'
        : 'http://localhost:3002/api/auth/register/provider';

      const body = userType === 'individual' ? {
        ...formData,
        emergencyContact: formData.nextOfKinName ? {
          name: formData.nextOfKinName,
          phone: formData.nextOfKinPhone,
          relationship: formData.nextOfKinRelationship
        } : undefined
      } : {
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        institutionName: providerFormData.institutionName,
        institutionType: providerFormData.institutionType,
        contactFirstName: providerFormData.contactFirstName,
        contactLastName: providerFormData.contactLastName,
        contactTitle: providerFormData.contactTitle,
        institutionAddress: {
          street: providerFormData.street,
          city: providerFormData.city,
          state: providerFormData.state,
          country: providerFormData.country,
          zipCode: providerFormData.zipCode,
        },
        phone: formData.phone,
        website: providerFormData.website || undefined,
        npiNumber: providerFormData.npiNumber || undefined,
        keyServices: providerFormData.keyServices || undefined
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (res.ok) {
        toastUtils.success('Welcome to CareVault!', 'Account created successfully.');
        setCurrentStep('complete');
      } else {
        const msg = data.errors ? data.errors.map((e: any) => e.message).join(', ') : data.message;
        toastUtils.error('Registration Failed', msg || 'Please try again.');
      }
    } catch {
      toastUtils.error('Network Error', 'Could not reach the server.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-bg-primary relative overflow-x-hidden selection:bg-[#D64463]/30">
      {/* Static Background - Removed heavy animations */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-[#D64463]/5 to-transparent" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#9C27B0]/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-24 w-64 h-64 bg-[#DEA319]/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] opacity-[0.03]" />
      </div>

      <div className="relative z-50">
        <UniversalHeader title="Create Account" />
      </div>

      <div className="flex-1 container mx-auto px-4 pt-24 pb-8 lg:pt-32 lg:pb-12 flex items-center justify-center relative z-0">
        <div className="w-full max-w-6xl flex flex-col lg:flex-row bg-white/95 backdrop-blur-sm rounded-[2.5rem] shadow-modern-xl border border-white/40 overflow-hidden">

          {/* Left Side - Interactive Branding */}
          <div className="hidden lg:flex w-[42%] relative overflow-hidden flex-col justify-between p-12 text-white bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#312E81]">
            <div className="absolute inset-0 bg-[url('/assets/mesh-dark.png')] opacity-20 mix-blend-overlay" />

            {/* Static Gradient Circles */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D64463] rounded-full mix-blend-multiply filter blur-[60px] opacity-20" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#9C27B0] rounded-full mix-blend-multiply filter blur-[60px] opacity-20" />

            <div className="relative z-10">
              <Link to="/" className="inline-flex items-center gap-2 mb-12 opacity-80 hover:opacity-100 transition-opacity">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <ArrowLeft size={16} />
                </div>
                <span className="text-sm font-medium tracking-wide">Back to Home</span>
              </Link>

              <div className="space-y-6">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/10 shadow-2xl mb-4">
                  {userType === 'individual' ? <ShieldCheck size={48} className="text-[#D64463]" /> : <Building2 size={48} className="text-[#9C27B0]" />}
                </div>

                <h2 className="text-4xl font-bold leading-tight">
                  {userType === 'individual' ?
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-white">Your Health,<br />Reimagined.</span> :
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-100 to-white">Healthcare<br />Excellence.</span>}
                </h2>

                <p className="text-blue-100/80 text-lg leading-relaxed max-w-sm">
                  {userType === 'individual'
                    ? 'Join thousands of users securing their future with CareVault\u2019s advanced personal health management.'
                    : 'Empower your institution with next-generation patient record systems and seamless coordination.'}
                </p>
              </div>
            </div>

            <div className="relative z-10 pt-12">
              <div className="flex gap-2 mb-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === 1 ? 'w-8 bg-[#D64463]' : 'w-2 bg-white/20'}`} />
                ))}
              </div>
              <div className="flex items-center gap-4 text-sm text-white/40 font-medium tracking-widest uppercase">
                <span>Secure</span>
                <span className="w-1 h-1 rounded-full bg-white/20" />
                <span>Private</span>
                <span className="w-1 h-1 rounded-full bg-white/20" />
                <span>Encrypted</span>
              </div>
            </div>
          </div>

          {/* Right Side - Form Area */}
          <div className="flex-1 p-8 lg:p-14 bg-white/50">

            <div className="max-w-xl mx-auto">
              {/* Using StepIndicator defined below */}
              <StepIndicator currentStep={currentStep} />

              <div className="text-center mb-10">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
                  {currentStep === 'account-type' ? 'Get Started' :
                    currentStep === 'account-details' ? (userType === 'individual' ? 'About You' : 'Institution Details') :
                      currentStep === 'security' ? 'Secure Account' : 'Welcome!'}
                </h1>
                <p className="text-gray-500">
                  {currentStep === 'account-type' ? 'Select the account type that suits you best.' :
                    currentStep === 'account-details' ? 'Please provide your accurate information.' :
                      currentStep === 'security' ? 'Set a strong password to protect your data.' : ''}
                </p>
              </div>

              {/* Step 1: Account Type */}
              {currentStep === 'account-type' && (
                <div className="space-y-6 animate-slide-in-right">
                  {[
                    { type: 'individual', icon: User, title: 'Individual Account', desc: 'For personal health management', color: 'bg-[#D64463]', border: 'peer-checked:border-[#D64463]' },
                    { type: 'provider', icon: Building2, title: 'Provider Account', desc: 'For hospitals and clinics', color: 'bg-[#9C27B0]', border: 'peer-checked:border-[#9C27B0]' }
                  ].map(({ type, icon: Icon, title, desc, color }) => (
                    <label key={type} className="relative block cursor-pointer group">
                      <input
                        type="radio"
                        name="userType"
                        className="peer sr-only"
                        checked={userType === type}
                        onChange={() => setUserType(type as 'individual' | 'provider')}
                      />
                      <div className={`
                        relative flex items-center gap-6 p-6 rounded-3xl border-2 border-transparent bg-white shadow-sm transition-all duration-300
                        ${userType === type ? 'ring-2 ring-offset-2 ring-offset-gray-50 ' + color.replace('bg-', 'ring-') : 'hover:border-gray-200 hover:shadow-md'}
                      `}>
                        <div className={`p-4 rounded-2xl transition-all duration-300 ${userType === type ? color + ' text-white shadow-lg scale-110' : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'}`}>
                          <Icon size={28} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`font-bold text-lg transition-colors ${userType === type ? 'text-gray-900' : 'text-gray-600'}`}>{title}</h3>
                          <p className="text-sm text-gray-400">{desc}</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${userType === type ? 'border-current text-green-500 scale-110' : 'border-gray-300'}`}>
                          {userType === type && <div className="w-3 h-3 rounded-full bg-current" />}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* Step 2: Account Details */}
              {currentStep === 'account-details' && (
                <div className="space-y-6 animate-slide-in-right">
                  {userType === 'individual' ? (
                    <>
                      <div className="grid grid-cols-2 gap-5">
                        <InputField label="First Name" value={formData.firstName} onChange={(v: string) => handleInputChange('firstName', v)} required />
                        <InputField label="Last Name" value={formData.lastName} onChange={(v: string) => handleInputChange('lastName', v)} required />
                      </div>
                      <InputField label="Email Address" type="email" value={formData.email} onChange={(v: string) => handleInputChange('email', v)} icon={<Mail />} required />
                      <div className="grid grid-cols-2 gap-5">
                        <InputField label="Date of Birth" type="date" value={formData.dateOfBirth} onChange={(v: string) => handleInputChange('dateOfBirth', v)} icon={<Calendar />} required />
                        <SelectField
                          label="Gender"
                          value={formData.gender}
                          onChange={(v: string) => handleInputChange('gender', v)}
                          options={[{ value: '', label: 'Select' }, { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]}
                        />
                      </div>
                      <InputField label="Phone Number" type="tel" value={formData.phone} onChange={(v: string) => handleInputChange('phone', v)} icon={<Phone />} optional placeholder="+234..." />

                      <SectionTitle icon={Heart}>Emergency Contact</SectionTitle>
                      <InputField label="Contact Name" value={formData.nextOfKinName} onChange={(v: string) => handleInputChange('nextOfKinName', v)} optional />
                      <div className="grid grid-cols-2 gap-5">
                        <InputField label="Relationship" value={formData.nextOfKinRelationship} onChange={(v: string) => handleInputChange('nextOfKinRelationship', v)} optional />
                        <InputField label="Phone" type="tel" value={formData.nextOfKinPhone} onChange={(v: string) => handleInputChange('nextOfKinPhone', v)} optional />
                      </div>
                    </>
                  ) : (
                    <>
                      <InputField label="Institution Name" value={providerFormData.institutionName} onChange={(v: string) => handleProviderInputChange('institutionName', v)} icon={<Building2 />} required />
                      <SelectField
                        label="Institution Type"
                        value={providerFormData.institutionType}
                        onChange={(v: string) => handleProviderInputChange('institutionType', v)}
                        required
                        options={[
                          { value: '', label: 'Select Type' },
                          { value: 'hospital', label: 'Hospital' },
                          { value: 'clinic', label: 'Clinic' },
                          { value: 'health_system', label: 'Health System' },
                          { value: 'pharmacy', label: 'Pharmacy' },
                          { value: 'diagnostic_center', label: 'Diagnostic Center' },
                          { value: 'other', label: 'Other' }
                        ]}
                      />
                      <InputField label="Email Address" type="email" value={formData.email} onChange={(v: string) => handleInputChange('email', v)} icon={<Mail />} required placeholder="your@email.com" />
                      <InputField label="Phone Number" type="tel" value={formData.phone} onChange={(v: string) => handleInputChange('phone', v)} icon={<Phone />} required placeholder="+234..." />

                      <SectionTitle required icon={User}>Contact Person</SectionTitle>
                      <div className="grid grid-cols-2 gap-5">
                        <InputField label="First Name" value={providerFormData.contactFirstName} onChange={(v: string) => handleProviderInputChange('contactFirstName', v)} required />
                        <InputField label="Last Name" value={providerFormData.contactLastName} onChange={(v: string) => handleProviderInputChange('contactLastName', v)} required />
                      </div>
                      <InputField label="Title / Role" value={providerFormData.contactTitle} onChange={(v: string) => handleProviderInputChange('contactTitle', v)} icon={<Briefcase />} required placeholder="e.g. Administrator" />

                      <SectionTitle required icon={MapPin}>Address</SectionTitle>
                      <InputField label="Street Address" value={providerFormData.street} onChange={(v: string) => handleProviderInputChange('street', v)} required />
                      <div className="grid grid-cols-2 gap-3">
                        <SelectField
                          label="Country"
                          value={providerFormData.country}
                          onChange={(v: string) => handleProviderInputChange('country', v)}
                          required
                          options={[
                            { value: '', label: 'Select Country' },
                            ...countries.map(c => ({ value: c.isoCode, label: c.name }))
                          ]}
                        />
                        <SelectField
                          label="State/Region"
                          value={providerFormData.state}
                          onChange={(v: string) => handleProviderInputChange('state', v)}
                          required
                          options={[
                            { value: '', label: states.length ? 'Select State' : 'Select country first' },
                            ...states.map(s => ({ value: s.name, label: s.name }))
                          ]}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <SelectField
                          label="City"
                          value={providerFormData.city}
                          onChange={(v: string) => handleProviderInputChange('city', v)}
                          required
                          options={[
                            { value: '', label: cities.length ? 'Select City' : 'Select state first' },
                            ...cities.map(c => ({ value: c.name, label: c.name }))
                          ]}
                        />
                        <InputField label="ZIP/Postal Code" value={providerFormData.zipCode} onChange={(v: string) => handleProviderInputChange('zipCode', v)} optional />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 3: Security */}
              {currentStep === 'security' && (
                <div className="space-y-6 animate-slide-in-right">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Password <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className="w-full bg-white/50 border border-gray-200 focus:border-[#D64463] focus:bg-white focus:ring-4 focus:ring-[#D64463]/10 rounded-2xl py-3.5 pl-5 pr-12 outline-none transition-all duration-300 text-gray-900"
                        placeholder="Min 8 characters"
                      />
                      <button onClick={() => setShowPassword(!showPassword)} type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D64463] transition-colors">
                        {showPassword ? <EyeOff size={20} strokeWidth={1.5} /> : <Eye size={20} strokeWidth={1.5} />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Confirm Password <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className="w-full bg-white/50 border border-gray-200 focus:border-[#D64463] focus:bg-white focus:ring-4 focus:ring-[#D64463]/10 rounded-2xl py-3.5 pl-5 pr-12 outline-none transition-all duration-300 text-gray-900"
                        placeholder="Re-enter password"
                      />
                      <button onClick={() => setShowConfirmPassword(!showConfirmPassword)} type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#D64463] transition-colors">
                        {showConfirmPassword ? <EyeOff size={20} strokeWidth={1.5} /> : <Eye size={20} strokeWidth={1.5} />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-blue-50/50 border border-blue-100 p-5 rounded-2xl flex gap-3">
                    <div className="mt-0.5 text-blue-500">
                      <ShieldCheck size={18} strokeWidth={2} />
                    </div>
                    <div className="space-y-1">
                      <h5 className="text-sm font-semibold text-blue-900">Password Strength</h5>
                      <p className="text-xs text-blue-700 leading-relaxed">
                        Must contain at least 8 characters, including uppercase, lowercase, number, and symbol.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Complete Step */}
              {currentStep === 'complete' && (
                <div className="text-center py-12 animate-fade-in-scale">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-200">
                    <CheckCircle size={48} className="text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Aboard!</h2>
                  <p className="text-gray-500 mb-10 max-w-sm mx-auto">Your account has been successfully created. Please check your email for verification.</p>
                  <Button onClick={() => navigate('/login')} className="w-full h-14 rounded-2xl bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:shadow-lg hover:shadow-[#D64463]/30 text-white font-bold text-lg transition-all transform hover:-translate-y-0.5">
                    Go to Login
                  </Button>
                </div>
              )}

              {/* Navigation Actions */}
              {currentStep !== 'complete' && (
                <div className="flex gap-4 mt-10">
                  {currentStep !== 'account-type' && (
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(prev => prev === 'security' ? 'account-details' : 'account-type')}
                      className="h-14 w-14 rounded-2xl border-gray-200 hover:bg-gray-50 flex items-center justify-center p-0 flex-shrink-0"
                    >
                      <ArrowLeft size={20} strokeWidth={1.5} />
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    disabled={isLoading}
                    className="flex-1 h-14 bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:shadow-lg hover:shadow-[#D64463]/30 text-white font-bold text-lg rounded-2xl transition-all transform hover:-translate-y-0.5"
                  >
                    {isLoading ? 'Processing...' : currentStep === 'security' ? 'Create Account' : 'Continue'}
                    {!isLoading && <ArrowRight size={20} className="ml-2" strokeWidth={2} />}
                  </Button>
                </div>
              )}

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-500">
                  Already have an account?{' '}
                  <Link to="/login" className="font-bold text-[#D64463] hover:text-[#9C27B0] transition-colors underline decoration-2 decoration-transparent hover:decoration-current">
                    Sign in here
                  </Link>
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 mt-auto bg-[#0F172A]">
        <UniversalFooter />
      </div>
    </div>
  );
};

// Sub-components moved outside to prevent re-renders and focus loss
const StepIndicator = ({ currentStep }: { currentStep: RegistrationStep }) => (
  <div className="flex justify-center items-center gap-4 mb-8">
    {['account-type', 'account-details', 'security'].map((step, idx) => {
      const stepIdx = ['account-type', 'account-details', 'security'].indexOf(currentStep);
      const isActive = idx === stepIdx;
      const isComplete = idx < stepIdx || currentStep === 'complete';

      return (
        <div key={step} className="flex items-center">
          <div className={`
            relative flex items-center justify-center w-10 h-10 rounded-full text-sm font-semibold transition-all duration-300
            ${isComplete ? 'bg-gradient-to-r from-[#D64463] to-[#9C27B0] text-white shadow-lg shadow-[#D64463]/20 scale-110' :
              isActive ? 'bg-white border-2 border-[#D64463] text-[#D64463] shadow-md shadow-[#D64463]/10 scale-110' :
                'bg-gray-100 text-gray-400 border border-gray-200'}
          `}>
            {isComplete ? <CheckCircle size={18} strokeWidth={2.5} /> : <span className="text-xs">{idx + 1}</span>}
            {isActive && (
              <span className="absolute -bottom-6 text-[10px] font-bold tracking-wider text-[#D64463] whitespace-nowrap uppercase">
                {step.replace('-', ' ')}
              </span>
            )}
          </div>
          {idx < 2 && (
            <div className={`w-12 h-0.5 mx-2 rounded-full transition-all duration-300 ${idx < stepIdx ? 'bg-gradient-to-r from-[#D64463] to-[#9C27B0]' : 'bg-gray-200'}`} />
          )}
        </div>
      );
    })}
  </div>
);

const InputField = ({ label, type = "text", value, onChange, icon, required, optional, maxLength, placeholder }: any) => (
  <div className="space-y-2 group">
    <label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-500 group-focus-within:text-[#D64463] transition-colors">
      {label}
      {required && <span className="text-red-500">*</span>}
      {optional && <span className="text-gray-400 text-[10px] font-normal lowercase">(optional)</span>}
    </label>
    <div className="relative transform transition-all duration-300 focus-within:scale-[1.01]">
      {icon && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D64463] transition-colors">
          {React.cloneElement(icon, { size: 18, strokeWidth: 1.5 })}
        </div>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        placeholder={placeholder}
        className={`
          w-full bg-white/50 border border-gray-200 backdrop-blur-sm
          focus:border-[#D64463] focus:bg-white focus:ring-4 focus:ring-[#D64463]/10 
          rounded-2xl py-3.5 ${icon ? 'pl-11' : 'pl-5'} pr-5 
          outline-none transition-all duration-300 text-gray-900 
          placeholder:text-gray-400/60 placeholder:font-light
          shadow-sm group-hover:shadow-md hover:border-gray-300
        `}
      />
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, options, required }: any) => (
  <div className="space-y-2 group">
    <label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-500 group-focus-within:text-[#D64463] transition-colors">
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative transform transition-all duration-300 focus-within:scale-[1.01]">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`
          w-full bg-white/50 border border-gray-200 backdrop-blur-sm
          focus:border-[#D64463] focus:bg-white focus:ring-4 focus:ring-[#D64463]/10 
          rounded-2xl py-3.5 px-5 outline-none transition-all duration-300 
          ${value ? 'text-gray-900' : 'text-gray-400/60 font-light'}
          shadow-sm group-hover:shadow-md hover:border-gray-300 appearance-none
        `}
      >
        {options.map((opt: any) => (
          <option key={opt.value} value={opt.value} className="text-gray-900 font-normal">{opt.label}</option>
        ))}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
        <ChevronRight size={16} strokeWidth={1.5} className="rotate-90" />
      </div>
    </div>
  </div>
);

const SectionTitle = ({ children, required, icon: Icon }: { children: React.ReactNode; required?: boolean; icon?: any }) => (
  <div className="flex items-center gap-3 pt-6 pb-4">
    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent flex-1" />
    <div className="flex items-center gap-2 text-gray-800 bg-white/60 px-4 py-1.5 rounded-full border border-gray-100 shadow-sm backdrop-blur-sm">
      {Icon && <Icon size={14} className="text-[#D64463]" strokeWidth={2} />}
      <h4 className="text-sm font-bold tracking-wide uppercase">{children}</h4>
      {required && <span className="text-red-500 text-xs">*</span>}
    </div>
    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent flex-1" />
  </div>
);

export default RegisterPage;
